import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TitularidadEnergiaViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-titularidad-energia-autocomplete",
    templateUrl: "./titularidad-energia-autocomplete.component.html",
    styleUrls: ["./titularidad-energia-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TitularidadEnergiaAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TitularidadEnergiaAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Titularidad de Energía";
	@Input() placeholder =
		"Buscar titularidad de energía por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de titularidad de energía";

	// Outputs
	@Output() titularidadEnergiaSelected =
		new EventEmitter<TitularidadEnergiaViewModel>();

	// State
	titularidadesEnergia: TitularidadEnergiaViewModel[] = [];
	titularidadEnergiaId: number | null = null;
	selectedTitularidadEnergia: TitularidadEnergiaViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTitularidadesEnergia();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.titularidadEnergiaId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de titularidades de energía
	 */
	private loadTitularidadesEnergia(): void {
		this.masterService
			.getTitularidadEnergiaList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (titularidadesEnergia) => {
					this.titularidadesEnergia = titularidadesEnergia;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las titularidades de energía",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTitularidadEnergia =
			this.titularidadEnergiaId != null
				? (this.titularidadesEnergia.find(
						(t) => t.id === this.titularidadEnergiaId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una titularidad de energía
	 */
	displayTitularidadEnergia = (
		titularidadEnergia: TitularidadEnergiaViewModel | string,
	): string => {
		if (typeof titularidadEnergia === "string") {
			return titularidadEnergia;
		}
		return titularidadEnergia &&
			titularidadEnergia.codigo &&
			titularidadEnergia.descripcion
			? `${titularidadEnergia.codigo} - ${titularidadEnergia.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(titularidadEnergia: TitularidadEnergiaViewModel | null): void {
		this.titularidadEnergiaId = titularidadEnergia
			? titularidadEnergia.id
			: null;
		this.selectedTitularidadEnergia = titularidadEnergia;
		this.onChange(this.titularidadEnergiaId);
		this.onTouched();
		if (titularidadEnergia) {
			this.titularidadEnergiaSelected.emit(titularidadEnergia);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
